<script>
export default {
  inheritAttrs: false,
}
</script>

<script setup>
/**
 * @description 文本溢出时才通过 title 属性展示完整内容的基础组件
 * @param {string} content - title 内容
 * @param {string} position - 兼容旧调用，原生 title 不使用
 * @param {number} line - 显示行数，默认为 1
 * @param {string} tag - 渲染的 HTML 标签，默认为 'div'
 */
import { ref, useAttrs, onMounted, onUnmounted, watch, nextTick } from 'vue'

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  position: {
    type: String,
    default: 'top',
  },
  line: {
    type: Number,
    default: 1,
  },
  tag: {
    type: String,
    default: 'div',
  },
})

const attrs = useAttrs()
const contentRef = ref(null)
const isDisabled = ref(true)
const OVERFLOW_TOLERANCE = 0.5

/**
 * @description 检查是否溢出并更新禁用状态
 */
const updateDisabledState = () => {
  const el = contentRef.value
  if (el) {
    const { width, height } = el.getBoundingClientRect()
    const visibleWidth = width || el.clientWidth
    const visibleHeight = height || el.clientHeight

    if (!props.content) {
      isDisabled.value = true
      return
    }
    if (props.line === 1) {
      isDisabled.value = el.scrollWidth - visibleWidth <= OVERFLOW_TOLERANCE
    } else {
      isDisabled.value = el.scrollHeight - visibleHeight <= OVERFLOW_TOLERANCE
    }
  }
}

/**
 * @description 鼠标移入时再次检查，确保万无一失
 */
const checkOverflow = () => {
  updateDisabledState()
}

onMounted(() => {
  updateDisabledState()
  if (import.meta.client) {
    window.addEventListener('resize', updateDisabledState)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', updateDisabledState)
  }
})

watch(
  () => [props.content, props.line],
  () => {
    nextTick(updateDisabledState)
  }
)
</script>

<template>
  <component
    :is="tag"
    ref="contentRef"
    class="base-overflow-tooltip"
    :class="[attrs.class, `line-${line}`]"
    :style="attrs.style"
    v-bind="attrs"
    :title="isDisabled ? undefined : content"
    @mouseenter="checkOverflow"
  >
    <slot />
  </component>
</template>

<style lang="scss" scoped>
.base-overflow-tooltip {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;

  &.line-1 {
    white-space: nowrap;
    display: block;
  }

  &.line-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.5;
  }

  &.line-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    line-height: 1.5;
  }
}
</style>
